import React from "react";
import dispatchBoardImg from "@/assets/catalog/service-line/scheduling-dispatch-board.webp";
import CpAccordion from "../../components/CpAccordion/CpAccordion";
import CpButton from "../../components/CpButton/CpButton";
import CpIcon from "../../components/CpIcon/CpIcon";
import CpLandingShell from "../../components/CpLandingShell/CpLandingShell";
import CpSectionHeading from "../../components/CpSectionHeading/CpSectionHeading";
import {
  CTA_HREF,
  FAQS,
  FEATURES,
  FEATURE_BGS,
  KEY_FEATURES,
  MODULES,
  STEPS,
  TIERS,
} from "./serviceLineContent";

const builtForChip: React.CSSProperties = {
  padding: "6px 14px",
  borderRadius: 999,
  background: "var(--white)",
  border: "1px solid var(--border-default)",
  fontSize: 13,
  fontWeight: 600,
};

const chipLabel: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "var(--grey-500)",
  marginRight: 4,
};

const proofIcon: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 12,
  background: "var(--ink-500)",
  display: "grid",
  placeItems: "center",
};

const PROOF_POINTS = [
  { icon: "layers", label: "One system, every team" },
  { icon: "zap", label: "Real-time job costing" },
  { icon: "rocket", label: "Onboarding at no extra cost" },
];

const ServiceLinePage: React.FC = () => (
  <CpLandingShell title="ServiceLine">
    <section
      data-screen-label="Hero"
      style={{
        background: "var(--cream-100)",
        padding: "clamp(56px, 7vw, 96px) 0",
      }}
    >
      <div
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 24px",
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 440px), 1fr))",
          gap: 56,
          alignItems: "center",
        }}
      >
        <div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 14px",
                borderRadius: 999,
                background: "var(--red-50)",
                border: "1px solid rgba(178,0,48,.15)",
                color: "var(--red-500)",
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                lineHeight: 1.2,
              }}
            >
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: "var(--red-500)",
                  boxShadow: "0 0 0 3px rgba(178,0,48,.18)",
                }}
              />
              Resources &amp; Programs
            </span>
          </div>
          <h1
            style={{
              margin: "22px 0 0",
              fontSize: "clamp(56px, 8vw, 104px)",
              lineHeight: 0.95,
              letterSpacing: "-.04em",
              fontWeight: 800,
              color: "var(--red-500)",
            }}
          >
            ServiceLine
          </h1>
          <p
            style={{
              margin: "10px 0 0",
              fontSize: "clamp(22px, 2.4vw, 30px)",
              fontWeight: 300,
              letterSpacing: "-.015em",
              lineHeight: 1.2,
              color: "var(--ink-500)",
            }}
          >
            CertainPath&apos;s All-in-One Field Service Software
          </p>
          <p
            style={{
              margin: "22px 0 0",
              maxWidth: "38ch",
              fontSize: "clamp(18px, 1.6vw, 21px)",
              lineHeight: 1.5,
              color: "var(--ink-500)",
            }}
          >
            From scheduling and dispatch to pricing, invoicing, and financial
            insights, our field service software gives contractors the tools to
            scale.
          </p>

          <div
            style={{
              marginTop: 26,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={chipLabel}>Built for</span>
            <span style={builtForChip}>HVAC</span>
            <span style={builtForChip}>Plumbing</span>
            <span style={builtForChip}>Electrical</span>
          </div>
          <div
            style={{
              marginTop: 12,
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <span style={chipLabel}>Available on</span>
            {TIERS.map((t) => (
              <span
                key={t}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 14px 6px 11px",
                  borderRadius: 999,
                  background: "var(--white)",
                  border: "1px solid var(--border-strong)",
                  fontSize: 13,
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                }}
              >
                <CpIcon color="var(--red-500)" name="plus" size={13} />
                {t}
                <span
                  style={{
                    marginLeft: 2,
                    paddingLeft: 8,
                    borderLeft: "1px solid var(--border-default)",
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: ".1em",
                    textTransform: "uppercase",
                    color: "var(--grey-500)",
                  }}
                >
                  Add-on
                </span>
              </span>
            ))}
          </div>

          <div
            style={{
              marginTop: 34,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <CpButton href={CTA_HREF} size="lg">
              Book a Software Discussion
            </CpButton>
            <CpButton href="#features" size="lg" variant="secondary">
              See What&apos;s Inside
            </CpButton>
          </div>

          <div
            style={{
              marginTop: 40,
              paddingTop: 24,
              borderTop: "1px solid var(--border-default)",
              display: "flex",
              gap: 28,
              flexWrap: "wrap",
            }}
          >
            {PROOF_POINTS.map((p) => (
              <div
                key={p.label}
                style={{ display: "flex", alignItems: "center", gap: 12 }}
              >
                <div style={proofIcon}>
                  <CpIcon color="var(--gold-400)" name={p.icon} size={20} />
                </div>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{p.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div
          aria-label="What's in ServiceLine"
          style={{
            background: "var(--ink-500)",
            borderRadius: 24,
            boxShadow: "var(--shadow-lg)",
            overflow: "hidden",
            color: "var(--cream-100)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "16px 24px",
              borderBottom: "1px solid var(--border-inverse)",
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 700 }}>
              One system for the whole company
            </span>
            <span
              style={{
                fontSize: 12,
                fontWeight: 800,
                letterSpacing: ".14em",
                textTransform: "uppercase",
                color: "var(--gold-400)",
              }}
            >
              ServiceLine
            </span>
          </div>
          <div style={{ padding: "8px 24px" }}>
            {MODULES.map((m) => (
              <a
                href={m.href}
                key={m.href}
                style={{
                  display: "grid",
                  gridTemplateColumns: "48px minmax(0, 1fr) 20px",
                  gap: 18,
                  alignItems: "center",
                  padding: "18px 0",
                  borderBottom: "1px solid var(--border-inverse)",
                  textDecoration: "none",
                  color: "var(--cream-100)",
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: "var(--ink-900)",
                    display: "grid",
                    placeItems: "center",
                  }}
                >
                  <CpIcon color="var(--gold-400)" name={m.icon} size={22} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 800,
                      letterSpacing: "-.01em",
                    }}
                  >
                    {m.title}
                  </div>
                  <div
                    style={{
                      marginTop: 3,
                      fontSize: 14,
                      fontWeight: 500,
                      color: "var(--ink-100)",
                    }}
                  >
                    {m.sub}
                  </div>
                </div>
                <CpIcon color="var(--ink-100)" name="arrow-down" size={18} />
              </a>
            ))}
          </div>
          <div
            style={{
              padding: "20px 24px 24px",
              background: "var(--ink-900)",
              display: "flex",
              alignItems: "center",
              gap: 14,
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "var(--gold-400)",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
              }}
            >
              <CpIcon color="var(--ink-500)" name="hard-hat" size={20} />
            </div>
            <span
              style={{
                fontSize: 15,
                fontWeight: 600,
                color: "var(--cream-100)",
              }}
            >
              Built by contractors, for contractors
            </span>
          </div>
        </div>
      </div>
    </section>

    <section
      data-screen-label="What Is ServiceLine"
      style={{
        background: "var(--ink-500)",
        padding: "clamp(56px, 7vw, 96px) 0",
      }}
    >
      <div
        style={{
          maxWidth: 1160,
          margin: "0 auto",
          padding: "0 24px",
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
          gap: "24px 64px",
          alignItems: "start",
        }}
      >
        <h2
          style={{
            margin: 0,
            color: "var(--gold-400)",
            fontSize: "clamp(30px, 3.2vw, 42px)",
            fontWeight: 800,
            letterSpacing: "-.025em",
            lineHeight: 1.12,
          }}
        >
          What is ServiceLine?
        </h2>
        <div>
          <p
            style={{
              margin: 0,
              color: "var(--cream-100)",
              fontSize: "clamp(20px, 1.9vw, 24px)",
              fontWeight: 300,
              lineHeight: 1.45,
            }}
          >
            You know the power of CertainPath in your business.{" "}
            <strong style={{ fontWeight: 800 }}>
              Now put it in your software.
            </strong>
          </p>
          <p
            style={{
              margin: "18px 0 0",
              color: "var(--cream-100)",
              fontSize: 17,
              lineHeight: 1.65,
            }}
          >
            ServiceLine is CertainPath&apos;s proprietary field service
            software. It connects your field team, your call center and your
            numbers in one system — so technicians focus on service, every call
            gets booked, and you know you&apos;re making money on every job.
          </p>
        </div>
      </div>
    </section>

    <section
      data-screen-label="Member Story Video"
      id="video"
      style={{ background: "var(--cream-100)", padding: "var(--section-y) 0" }}
    >
      <div style={{ maxWidth: 1080, margin: "0 auto", padding: "0 24px" }}>
        <CpSectionHeading
          align="center"
          eyebrow="Member Story"
          title="See How CertainPath Software Helped Hudson Plumbing"
        />
        <div
          style={{
            marginTop: 40,
            borderRadius: 24,
            overflow: "hidden",
            background: "var(--ink-500)",
            boxShadow: "var(--shadow-lg)",
            aspectRatio: "16 / 9",
          }}
        >
          <iframe
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            src="https://www.youtube-nocookie.com/embed/QsncpaHxB9g?rel=0"
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              border: 0,
            }}
            title="CertainPath Software — Hudson Plumbing story"
          />
        </div>
      </div>
    </section>

    <div id="features">
      {FEATURES.map((f, i) => (
        <section
          data-screen-label={f.eyebrow}
          id={f.id}
          key={f.id}
          style={{ background: FEATURE_BGS[i], padding: "var(--section-y) 0" }}
        >
          <div
            style={{
              maxWidth: "var(--container-max)",
              margin: "0 auto",
              padding: "0 24px",
              display: "grid",
              gridTemplateColumns:
                "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
              gap: 56,
              alignItems: "center",
            }}
          >
            <div style={{ order: i % 2 ? 2 : 1 }}>
              <CpSectionHeading
                eyebrow={f.eyebrow}
                lead={f.lead}
                title={f.title}
              />
              <ul
                style={{
                  listStyle: "none",
                  margin: "28px 0 0",
                  padding: 0,
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
                  gap: "12px 24px",
                }}
              >
                {f.points.map((p) => (
                  <li
                    key={p}
                    style={{
                      display: "flex",
                      gap: 10,
                      alignItems: "flex-start",
                      fontSize: 16,
                      fontWeight: 600,
                      lineHeight: 1.45,
                    }}
                  >
                    <CpIcon
                      color="var(--red-500)"
                      name="circle-check"
                      size={20}
                    />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div style={{ order: i % 2 ? 1 : 2 }}>
              <div
                style={{
                  background: "var(--sand-200)",
                  border: "1px solid var(--border-default)",
                  borderRadius: 24,
                  padding: "clamp(20px, 3vw, 36px)",
                  boxShadow: "var(--shadow-md)",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <img
                  alt={f.alt}
                  loading="lazy"
                  src={f.img}
                  style={{
                    display: "block",
                    width: "100%",
                    maxHeight: 520,
                    height: "auto",
                    objectFit: "contain",
                    borderRadius: 12,
                  }}
                />
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>

    <section
      data-screen-label="Key Features"
      id="key-features"
      style={{ background: "var(--sand-200)", padding: "var(--section-y) 0" }}
    >
      <div
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 24px",
        }}
      >
        <CpSectionHeading
          align="center"
          eyebrow="Key Features"
          lead="Discover the tools that make running your contracting business easier, more efficient and more profitable."
          title="Key Features of CertainPath Software"
        />
        <div
          style={{
            marginTop: 48,
            background: "var(--white)",
            border: "1px solid var(--border-default)",
            borderRadius: 24,
            padding: "clamp(16px, 2.5vw, 28px)",
            boxShadow: "var(--shadow-md)",
          }}
        >
          <img
            alt="CertainPath Software scheduling and dispatch board"
            loading="lazy"
            src={dispatchBoardImg}
            style={{
              display: "block",
              width: "100%",
              height: "auto",
              borderRadius: 12,
            }}
          />
        </div>
        <div
          data-stagger=""
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
            gap: 16,
            marginTop: 24,
          }}
        >
          {KEY_FEATURES.map((k) => (
            <div
              key={k.title}
              style={{
                background: "var(--white)",
                border: "1px solid var(--border-default)",
                borderRadius: 16,
                padding: 24,
                display: "grid",
                gridTemplateColumns: "48px minmax(0, 1fr)",
                gap: 18,
                alignItems: "start",
                boxShadow: "var(--shadow-sm)",
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: "var(--ink-500)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <CpIcon color="var(--gold-400)" name={k.icon} size={22} />
              </div>
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 18,
                    lineHeight: 1.3,
                    letterSpacing: "-.01em",
                  }}
                >
                  {k.title}
                </h3>
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: 15,
                    lineHeight: 1.55,
                    color: "var(--text-muted)",
                    textWrap: "pretty",
                  }}
                >
                  {k.body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section
      data-screen-label="Implementation"
      id="implementation"
      style={{ background: "var(--ink-500)", padding: "var(--section-y) 0" }}
    >
      <div
        style={{
          maxWidth: "var(--container-max)",
          margin: "0 auto",
          padding: "0 24px",
        }}
      >
        <CpSectionHeading
          align="center"
          eyebrow="Implementation"
          lead="Any solution you use has to be easy to use and easy to implement. From day one, you're not doing it alone."
          title="Get Up and Running Fast with Easy to Use Features"
          tone="dark"
        />
        <ol
          data-stagger=""
          style={{
            listStyle: "none",
            margin: "48px 0 0",
            padding: 0,
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
            gap: 20,
          }}
        >
          {STEPS.map((s) => (
            <li
              key={s.title}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 18,
                textAlign: "center",
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  border: "2px solid var(--gold-400)",
                  display: "grid",
                  placeItems: "center",
                  boxSizing: "border-box",
                }}
              >
                <CpIcon color="var(--gold-400)" name={s.icon} size={24} />
              </div>
              <div
                style={{
                  background: "var(--white)",
                  borderRadius: 16,
                  padding: "26px 22px",
                  width: "100%",
                  boxSizing: "border-box",
                  flex: 1,
                }}
              >
                <h3 style={{ margin: 0, fontSize: 18, lineHeight: 1.3 }}>
                  {s.title}
                </h3>
                <p
                  style={{
                    margin: "10px 0 0",
                    fontSize: 15,
                    lineHeight: 1.55,
                    color: "var(--text-muted)",
                  }}
                >
                  {s.body}
                </p>
              </div>
            </li>
          ))}
        </ol>
        <div
          style={{ marginTop: 48, display: "flex", justifyContent: "center" }}
        >
          <CpButton href={CTA_HREF} size="lg">
            Book a Software Discussion
          </CpButton>
        </div>
      </div>
    </section>

    <section
      data-screen-label="FAQ"
      id="faq"
      style={{
        background: "var(--sand-200)",
        padding: "var(--section-y) 0 48px",
      }}
    >
      <div style={{ maxWidth: 920, margin: "0 auto", padding: "0 24px" }}>
        <CpSectionHeading
          align="center"
          eyebrow="Questions"
          title="Frequently Asked Questions"
        />
        <div style={{ marginTop: 40 }}>
          <CpAccordion items={FAQS} />
        </div>
      </div>
    </section>

    <section
      data-screen-label="CTA"
      style={{
        background: "var(--sand-200)",
        padding: "48px 0 var(--section-y)",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
        <div
          style={{
            background: "var(--gradient-brand)",
            borderRadius: 24,
            padding: "clamp(56px, 7vw, 88px) clamp(24px, 5vw, 56px)",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 18,
            boxShadow: "var(--shadow-lg)",
          }}
        >
          <h2
            style={{
              margin: 0,
              color: "var(--cream-100)",
              fontSize: "var(--text-display-2)",
              lineHeight: 1.08,
              letterSpacing: "-.025em",
              maxWidth: "20ch",
              textWrap: "balance",
            }}
          >
            See What ServiceLine Can Do for You
          </h2>
          <p
            style={{
              margin: "0 0 14px",
              color: "var(--cream-100)",
              fontSize: 18,
              lineHeight: 1.55,
              maxWidth: "54ch",
            }}
          >
            Book a time with our team to walk through CertainPath Software and
            how it fits your company.
          </p>
          <CpButton href={CTA_HREF} size="lg" variant="inverse">
            Book a Software Discussion
          </CpButton>
          <p
            style={{
              margin: "8px 0 0",
              color: "var(--cream-100)",
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            CertainPath Software isn&apos;t currently configured for roofing
            companies.
          </p>
        </div>
      </div>
    </section>
  </CpLandingShell>
);

export default ServiceLinePage;
